import type { PMTileSource } from './types/dataModel'
import type { ViewState } from './util/viewState'
import TimeSeriesIcon from '~/assets/icons/graph-time-series.svg?react'
import HistoricalIcon from '~/assets/icons/graph-historical.svg?react'
import DirectionIcon from '~/assets/icons/graph-direction.svg?react'
import BoxPlotIcon from '~/assets/icons/graph-boxplot.svg?react'
import WaveRoseIcon from '~/assets/icons/graph-waverose.svg?react'
import EvaIcon from '~/assets/icons/graph-eva.svg?react'
import {
  BATHYMETRY_COG_OBJECT,
  FLOOD_CONTOUR_S3_OBJECTS,
  PMTILE_S3_OBJECTS,
  cogUrl,
  pmTilesUrl,
} from './cdnAssets'

export {
  CDN_DOMAIN,
  FLOOD_CONTOUR_DATE,
  NAUTILUS_STORAGE_DOMAIN,
  SPATIALOPS_PREFIX,
  VIDEO_BUCKET,
} from './cdnAssets'
// consult the `infrastructure_admin` repo to change bucket permissions
export { GEODATALAKE_BUCKET as SPATIALOPS_BUCKET } from './cdnAssets'

export const MAP_FLY_DURATION_MS = 1500

export const COSMOS_SITES = ['santacruz', 'ventura', 'halfmoonbay'] as const
export type CosmosSite = (typeof COSMOS_SITES)[number]

/** Sites with published sedero rasters. Half Moon Bay has no erosion dataset. */
export const EROSION_SITES = ['santacruz', 'ventura'] as const
export type ErosionSite = (typeof EROSION_SITES)[number]

export function isErosionSite(site: CosmosSite): site is ErosionSite {
  return (EROSION_SITES as readonly string[]).includes(site)
}

export const DATASETS = ['cosmos', 'erosion', 'waves'] as const
export type Dataset = (typeof DATASETS)[number]

const COSMOS_VIEW_PADDING = { top: 0, right: 0, bottom: 0, left: 0 } as const
const COSMOS_SITE_ZOOM = 12.75

export const COSMOS_SITE_BOUNDS = {
  santacruz: [-122.052823, 36.948833, -121.922017, 36.984251],
  ventura: [-119.32376, 34.155509, -119.21604, 34.284896],
  halfmoonbay: [-122.514985, 37.418135, -122.433731, 37.520753],
} as const satisfies Record<CosmosSite, readonly [number, number, number, number]>

function viewStateFromBounds(bounds: readonly [number, number, number, number]): ViewState {
  const [minLng, minLat, maxLng, maxLat] = bounds
  return {
    longitude: (minLng + maxLng) / 2,
    latitude: (minLat + maxLat) / 2,
    zoom: COSMOS_SITE_ZOOM,
    pitch: 0,
    bearing: 0,
    padding: COSMOS_VIEW_PADDING,
  }
}

export const COSMOS_SITE_VIEW_STATES: Record<CosmosSite, ViewState> = {
  santacruz: viewStateFromBounds(COSMOS_SITE_BOUNDS.santacruz),
  ventura: viewStateFromBounds(COSMOS_SITE_BOUNDS.ventura),
  halfmoonbay: viewStateFromBounds(COSMOS_SITE_BOUNDS.halfmoonbay),
}

export const INITIAL_VIEW_STATE: ViewState = COSMOS_SITE_VIEW_STATES.santacruz

export const CALIFORNIA_COAST_VIEW_STATE: ViewState = {
  longitude: -120.5,
  latitude: 36.5,
  zoom: 5.5,
  pitch: 0,
  bearing: 0,
  padding: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
} as const

export const PMTileSources = {
  BATHYMETRY: 'bathymetry',
  FLOODPLAINS_SANTA_CRUZ: 'floodplains_santacruz',
  FLOODPLAINS_VENTURA: 'floodplains_ventura',
  FLOODPLAINS_HALF_MOON_BAY: 'floodplains_halfmoonbay',
  BUILDING_SANTA_CRUZ: 'building_damages_santacruz',
  BUILDING_VENTURA: 'building_damages_ventura',
  BUILDING_HALF_MOON_BAY: 'building_damages_halfmoonbay',
  ROAD_SANTA_CRUZ: 'road_flooding_santacruz',
  ROAD_VENTURA: 'road_flooding_ventura',
  ROAD_HALF_MOON_BAY: 'road_flooding_halfmoonbay',
  ADAPTATION_SANTA_CRUZ: 'adaptation_santacruz',
  ADAPTATION_VENTURA: 'adaptation_ventura',
  ADAPTATION_HALF_MOON_BAY: 'adaptation_halfmoonbay',
  WAVES: 'waves',
} as const

/** Contour layer source ids (not in PMTileURLs / usePMTiles — loaded only by ContourFloodLayer). */
export const FloodContourSources = {
  SANTA_CRUZ: 'flood_contours_santacruz',
  VENTURA: 'flood_contours_ventura',
  HALF_MOON_BAY: 'flood_contours_halfmoonbay',
} as const

export type FloodContourSource = (typeof FloodContourSources)[keyof typeof FloodContourSources]

export const LayerId = {
  /** Stable beforeId target — Santa Cruz fill is always mounted first. */
  BUILDING: `${PMTileSources.BUILDING_SANTA_CRUZ}-fill`,
  FLOOD_1: `floodplains-0`,
  FLOOD_2: `floodplains-1`,
  EROSION: 'erosion_sedero',
} as const

export const MaptilerLayerId = {
  COUNTRY: 'Country labels',
  STATE: 'State labels',
}

export const VisibleLayers = {
  FLOOD: 'flood',
  BUILDING: 'building',
  ROAD: 'road',
  ADAPTATION: 'adaptation',
  WAVES: 'wave',
  BATHYMETRY: 'bathymetry',
  EROSION: 'erosion',
} as const

/** Neutral context styling for buildings and roads in the erosion view. */
export const EROSION_CONTEXT_COLORS = {
  building: '#4A4A4A',
  buildingOutline: '#2F2F2F',
  road: '#3D3D3D',
} as const

/** Ordered list of building occupancy category keys (HAZUS-style). */
export const BUILDING_CATEGORIES = [
  'Residential',
  'Commercial',
  'Industrial',
  'Government',
  'Educational',
  'Agriculture',
  'Religious',
] as const

export type BuildingCategory = (typeof BUILDING_CATEGORIES)[number]

/** Distinct hex color per building category. Includes Unknown for unmapped/fallback. */
export const BUILDING_CATEGORY_COLORS: Record<BuildingCategory | 'Unknown', string> = {
  Residential: '#D0021B',
  Commercial: '#F5A623',
  Industrial: '#7ED321',
  Government: '#BD10E0',
  Educational: '#50E3C2',
  Agriculture: '#B8E986',
  Religious: '#4A90D9',
  Unknown: '#9e9e9e',
}

/** Simplified legend: Commercial, Residential, and Other (all remaining categories). */
export const BUILDING_LEGEND_CATEGORIES = ['Commercial', 'Residential', 'Other'] as const
export type BuildingLegendCategory = (typeof BUILDING_LEGEND_CATEGORIES)[number]

export const BUILDING_LEGEND_COLORS: Record<BuildingLegendCategory, string> = {
  Commercial: BUILDING_CATEGORY_COLORS.Commercial,
  Residential: BUILDING_CATEGORY_COLORS.Residential,
  Other: BUILDING_CATEGORY_COLORS.Unknown,
}

export const BATHYMETRY_DEPTH_COLORS = [
  [0, '#CFD8DC'], // pale slate
  [-50, '#90A4AE'], // slate
  [-200, '#607D8B'], // blue-slate
  [-500, '#455A64'], // dark slate
  [-1000, '#263238'], // charcoal slate
] as const

export const COSMOS_RETURN_PERIODS = ['005', '020', '050', '100']
export const DEM_SCENARIOS = ['020', '050', '080']
export const ADAPTATION_OPTIONS = [
  { value: '000', label: 'None' },
  { value: '014', label: 'Seawall' },
  { value: '023', label: 'Reef' },
  { value: '031', label: 'Dune' },
] as const
export type AdaptationCode = (typeof ADAPTATION_OPTIONS)[number]['value']
export const ADAPTATION_SCENARIOS = ADAPTATION_OPTIONS.map((option) => option.value)

/** UI adaptation codes available per site (Half Moon Bay has no dune data). */
export const COSMOS_SITE_ADAPTATION_CODES: Record<CosmosSite, readonly AdaptationCode[]> = {
  santacruz: ['000', '014', '023', '031'],
  ventura: ['000', '014', '023', '031'],
  halfmoonbay: ['000', '014', '023'],
}

export function adaptationOptionsForSite(site: CosmosSite) {
  const codes = COSMOS_SITE_ADAPTATION_CODES[site]
  return ADAPTATION_OPTIONS.filter((option) => codes.includes(option.value))
}

/** Normalized flood tile `adaptation` attribute (uppercase). */
export const FLOOD_TILE_ADAPTATION: Record<AdaptationCode, string> = {
  '000': 'NONE',
  '014': 'SEAWALL',
  '023': 'REEF',
  '031': 'DUNE',
}

export const FLOOD_TILE_DEM: Record<(typeof DEM_SCENARIOS)[number], string> = {
  '020': '2025',
  '050': '2055',
  '080': '2085',
}

export const FLOOD_TILE_RETURN_PERIOD: Record<(typeof COSMOS_RETURN_PERIODS)[number], string> = {
  '005': '5',
  '020': '20',
  '050': '50',
  '100': '100',
}

/** Normalized adaptation overlay tile `adaptation` attribute (lowercase). */
export const ADAPTATION_OVERLAY_TYPE: Record<AdaptationCode, string> = {
  '000': 'none',
  '014': 'seawall',
  '023': 'reef',
  '031': 'dune',
}

/** Shared by the adaptation map overlay and its legend icons so the two cannot drift. */
export const ADAPTATION_COLORS = {
  base: '#8B4513',
  outline: '#5F2F0D',
  detail: '#D9A06A',
} as const

export const BASEMAP_STYLES = {
  // SWAP THAT (out on the floor)
  FLAT: import.meta.env?.VITE_OFFLINE_BUILD
    ? '/light_basemap_style.json'
    : 'https://api.maptiler.com/maps/0197229d-ea80-7c56-8410-8711d629e4b9/style.json?key=kA23mbacz1UyORj2qBRz',
  SATELLITE: import.meta.env?.VITE_OFFLINE_BUILD
    ? '/style.json'
    : 'https://api.maptiler.com/maps/0197e59d-418a-7810-a9c4-017e992114dd/style.json?key=kA23mbacz1UyORj2qBRz',
} as const

export type BasemapType = keyof typeof BASEMAP_STYLES

export const SCENARIO_LESS_BAD_FILL_COLOR = '#00b4d8'

export const MAIN_MAP_ID = 'main-map'
export const MAX_VIEW_STATE_UPDATE_THROTTLE_MS = 100
export const DEFAULT_MAX_ZOOM = import.meta.env?.DEV ? undefined : 13.5

export const FLOODPLAINS_SOURCES = [
  PMTileSources.FLOODPLAINS_SANTA_CRUZ,
  PMTileSources.FLOODPLAINS_VENTURA,
  PMTileSources.FLOODPLAINS_HALF_MOON_BAY,
] as const

export const ADAPTATION_SOURCES = [
  PMTileSources.ADAPTATION_SANTA_CRUZ,
  PMTileSources.ADAPTATION_VENTURA,
  PMTileSources.ADAPTATION_HALF_MOON_BAY,
] as const

export const BUILDING_SOURCES = [
  PMTileSources.BUILDING_SANTA_CRUZ,
  PMTileSources.BUILDING_VENTURA,
  PMTileSources.BUILDING_HALF_MOON_BAY,
] as const

export const BUILDING_SOURCE_BY_SITE: Record<CosmosSite, (typeof BUILDING_SOURCES)[number]> = {
  santacruz: PMTileSources.BUILDING_SANTA_CRUZ,
  ventura: PMTileSources.BUILDING_VENTURA,
  halfmoonbay: PMTileSources.BUILDING_HALF_MOON_BAY,
}

export const ROAD_SOURCES = [
  PMTileSources.ROAD_SANTA_CRUZ,
  PMTileSources.ROAD_VENTURA,
  PMTileSources.ROAD_HALF_MOON_BAY,
] as const

export const ROAD_SOURCE_BY_SITE: Record<CosmosSite, (typeof ROAD_SOURCES)[number]> = {
  santacruz: PMTileSources.ROAD_SANTA_CRUZ,
  ventura: PMTileSources.ROAD_VENTURA,
  halfmoonbay: PMTileSources.ROAD_HALF_MOON_BAY,
}

export function isBuildingPmTileSource(source: string): boolean {
  return (BUILDING_SOURCES as readonly string[]).includes(source)
}

/** MapLibre layer id for a building damages fill (one per site PMTiles source). */
export function buildingLayerId(source: (typeof BUILDING_SOURCES)[number] | string): string {
  return `${source}-fill`
}

export function isBuildingLayerId(layerId: string | undefined): boolean {
  if (!layerId) return false
  return BUILDING_SOURCES.some((source) => layerId === buildingLayerId(source))
}

export function cosmosSiteForBuildingSource(source: string): CosmosSite | undefined {
  return (Object.entries(BUILDING_SOURCE_BY_SITE) as [CosmosSite, string][]).find(
    ([, s]) => s === source
  )?.[0]
}

export function isFloodContourSource(source: string): boolean {
  return (Object.values(FloodContourSources) as string[]).includes(source)
}

/** Contour PMTiles (depth bands) — separate from extent floodplains; not in usePMTiles fetch. */
export const FLOOD_CONTOUR_SOURCES = [
  FloodContourSources.SANTA_CRUZ,
  FloodContourSources.VENTURA,
  FloodContourSources.HALF_MOON_BAY,
] as const

export const FLOOD_CONTOUR_SOURCE_LAYER = 'flood_contours'

/**
 * Contour product: 6 fixed 0.5 m buckets (<0.5 … >2.5).
 * Must stay in sync with processing/flood_contour_config.yaml `breaks`.
 */
export const FLOOD_CONTOUR_URLS: Record<FloodContourSource, string> = {
  [FloodContourSources.SANTA_CRUZ]: pmTilesUrl(FLOOD_CONTOUR_S3_OBJECTS.flood_contours_santacruz),
  [FloodContourSources.VENTURA]: pmTilesUrl(FLOOD_CONTOUR_S3_OBJECTS.flood_contours_ventura),
  [FloodContourSources.HALF_MOON_BAY]: pmTilesUrl(
    FLOOD_CONTOUR_S3_OBJECTS.flood_contours_halfmoonbay
  ),
}

export function isAdaptationPmTileSource(source: string): boolean {
  return (ADAPTATION_SOURCES as readonly string[]).includes(source)
}

export function isAdaptationLayerId(layerId: string | undefined): boolean {
  if (!layerId) return false
  return ADAPTATION_SOURCES.some((source) => layerId === `${source}-line`)
}

export const PMTileURLs: Record<PMTileSource, string> = {
  [PMTileSources.FLOODPLAINS_SANTA_CRUZ]: pmTilesUrl(PMTILE_S3_OBJECTS.floodplains_santacruz),
  [PMTileSources.FLOODPLAINS_VENTURA]: pmTilesUrl(PMTILE_S3_OBJECTS.floodplains_ventura),
  [PMTileSources.FLOODPLAINS_HALF_MOON_BAY]: pmTilesUrl(PMTILE_S3_OBJECTS.floodplains_halfmoonbay),
  [PMTileSources.BUILDING_SANTA_CRUZ]: pmTilesUrl(PMTILE_S3_OBJECTS.building_damages_santacruz),
  [PMTileSources.BUILDING_VENTURA]: pmTilesUrl(PMTILE_S3_OBJECTS.building_damages_ventura),
  [PMTileSources.BUILDING_HALF_MOON_BAY]: pmTilesUrl(
    PMTILE_S3_OBJECTS.building_damages_halfmoonbay
  ),
  [PMTileSources.ROAD_SANTA_CRUZ]: pmTilesUrl(PMTILE_S3_OBJECTS.road_flooding_santacruz),
  [PMTileSources.ROAD_VENTURA]: pmTilesUrl(PMTILE_S3_OBJECTS.road_flooding_ventura),
  [PMTileSources.ROAD_HALF_MOON_BAY]: pmTilesUrl(PMTILE_S3_OBJECTS.road_flooding_halfmoonbay),
  [PMTileSources.ADAPTATION_SANTA_CRUZ]: pmTilesUrl(PMTILE_S3_OBJECTS.adaptation_santacruz),
  [PMTileSources.ADAPTATION_VENTURA]: pmTilesUrl(PMTILE_S3_OBJECTS.adaptation_ventura),
  [PMTileSources.ADAPTATION_HALF_MOON_BAY]: pmTilesUrl(PMTILE_S3_OBJECTS.adaptation_halfmoonbay),
  [PMTileSources.WAVES]: pmTilesUrl(PMTILE_S3_OBJECTS.waves),
  [PMTileSources.BATHYMETRY]: pmTilesUrl(PMTILE_S3_OBJECTS.bathymetry),
} as const

export const BATHYMETRY_COG_URL = cogUrl(BATHYMETRY_COG_OBJECT)

export const COLORMAP_RED = ['#ffffb2', '#fecc5c', '#fd8d3c', '#f03b20', '#bd0026']
export const COLORMAP_YELLOW = ['#EDDD53', '#91CAE3', '#1895C7']
/** Sequential purple for averted damages / adaptation benefits (ColorBrewer Purples-5). */
export const COLORMAP_PURPLE_BENEFITS = ['#f2f0f7', '#cbc9e2', '#9e9ac8', '#756bb1', '#54278f']

/** Flood depth colormap (shallow → deep). */
export const FLOOD_DEPTH_COLORMAP = [
  '#caf0f8',
  '#90e0ef',
  '#48cae4',
  '#00b4d8',
  '#0096c7',
  '#0077b6',
  '#023e8a',
  '#03045e',
  '#012a4a',
  '#001219',
] as const

/**
 * Contour edges for 6 buckets: <0.5, 0.5–1, 1–1.5, 1.5–2, 2–2.5, >2.5.
 * First entry is min_depth; last is max_depth (top band also covers deeper).
 * Must stay in sync with processing/flood_contour_config.yaml `breaks`.
 */
export const FLOOD_DEPTH_BREAKS = [0.1, 0.5, 1.0, 1.5, 2.0, 2.5, 3.0] as const
// Diverging colormap: blue (negative) -> white (zero) -> red (positive)
export const COLORMAP_DIVERGING_RWB = ['#4575b4', '#ffffff', '#d73027']
// Enhanced 5-color version with saturated intermediates for faster color transitions
export const COLORMAP_DIVERGING_RWB_ENHANCED = [
  '#4575b4',
  '#6a9fd4',
  '#ffffff',
  '#e8755b',
  '#d73027',
]

/**
 * Bed-level change class edges in metres, ascending. 8 edges → 9 classes.
 * Banded rather than a linear stretch because the distribution is sharply peaked at zero
 * (~46% of valid pixels move less than 5 cm); a continuous ramp renders as "no change".
 */
export const EROSION_BREAKS_M = [-2.0, -1.0, -0.5, -0.2, 0.2, 0.5, 1.0, 2.0] as const

/**
 * ColorBrewer RdBu-9. Negative = erosion (warm), positive = deposition (cool), matching the
 * sedero sign convention. The near-zero class is a translucent neutral so it stays
 * distinguishable from nodata (fully transparent) without dominating the map.
 * Ordered erosion → deposition, one entry per EROSION_BREAKS_M class.
 */
export const EROSION_COLORMAP = [
  { hex: '#b2182b', alpha: 255 }, //        change < -2.0   erosion, clamped
  { hex: '#d6604d', alpha: 255 }, // -2.0 ≤ change < -1.0
  { hex: '#f4a582', alpha: 255 }, // -1.0 ≤ change < -0.5
  { hex: '#fddbc7', alpha: 255 }, // -0.5 ≤ change < -0.2
  { hex: '#f0f0f0', alpha: 90 }, // -0.2 ≤ change < 0.2    no meaningful change
  { hex: '#d1e5f0', alpha: 255 }, //  0.2 ≤ change < 0.5
  { hex: '#92c5de', alpha: 255 }, //  0.5 ≤ change < 1.0
  { hex: '#4393c3', alpha: 255 }, //  1.0 ≤ change < 2.0
  { hex: '#2166ac', alpha: 255 }, //  2.0 ≤ change          deposition, clamped
] as const

// COLORMAP_YELLOW[0] and COLORMAP_YELLOW[2] in RGB.
export const YELLOW = [237, 221, 83] as const
export const BLUE = [24, 149, 199] as const

export const BUILDING_DAMAGES_BREAKS = [0, 10_000, 100_000, 1_000_000, 10_000_000]

/** Averted damages saturate an order of magnitude earlier so smaller benefits stay legible. */
export const AVERTED_DAMAGES_BREAKS = [0, 1_000, 10_000, 100_000, 1_000_000]

export type GraphId =
  | 'reconstructed-time-series-3h'
  | 'annual-wave-height-boxplot'
  | 'wave-rose'
  | 'hs-tp-density'
  | 'hs-dir-density'
  | 'extreme-value-analysis-gpd'

export type GraphProperties = {
  label: string
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  /** Filename suffix appended to point_name to build the HTML file path. */
  htmlSuffix: string
  /** Iframe height class override for graphs that need more vertical space (e.g. stacked subplots). */
  heightClass?: string
}

export const GRAPHS: Record<GraphId, GraphProperties> = {
  'reconstructed-time-series-3h': {
    label: 'Reconstructed Time Series',
    Icon: TimeSeriesIcon,
    htmlSuffix: '_csiro_F001A_reconstructed_time_series_1000_3h.html',
  },
  'annual-wave-height-boxplot': {
    label: 'Annual Wave Height (Box Plot)',
    Icon: BoxPlotIcon,
    htmlSuffix: '_csiro_F005_annual_wave_height_boxplot.html',
  },
  'wave-rose': {
    label: 'Wave Rose',
    Icon: WaveRoseIcon,
    htmlSuffix: '_csiro_F006_waverose.html',
  },
  'hs-tp-density': {
    label: 'Hₛ & Peak Period (Density)',
    Icon: HistoricalIcon,
    htmlSuffix: '_csiro_F007_hs_tp_density.html',
  },
  'hs-dir-density': {
    label: 'Hₛ & Direction (Density)',
    Icon: DirectionIcon,
    htmlSuffix: '_csiro_F007B_hs_dir_density.html',
  },
  'extreme-value-analysis-gpd': {
    label: 'Extreme Value Analysis',
    Icon: EvaIcon,
    htmlSuffix: '_csiro_F008C_eva_pot_gpd_rps.html',
  },
}

export const WAVE_UNITS = {
  DirMEF: '°',
  Hs12: 'm',
  Hs_q99: 'm',
  Hs_q99_mean_percentage_change: '%',
  Hsmean: 'm',
  Hsmean_mean_percentage_change: '%',
  WPmean_mean_percentage_change: '%',
  WPsum: 'W/m',

  extremes_energy_flux_averaged_direction: '°',

  hs_rp_100: 'm',
  hs_rp_100_mean_percentage_change: '%',
  hs_rp_50: 'm',

  // ACCESS-CM2 (CM2)
  'ratio_rp_100_2055_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_100_2085_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_10_2055_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_10_2085_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_20_2055_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_20_2085_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_2_2055_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_2_2085_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_50_2055_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_50_2085_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_5_2055_2025_ACCESS-CM2_percentage_change': '%',
  'ratio_rp_5_2085_2025_ACCESS-CM2_percentage_change': '%',

  // EC-EARTH3 (E3)
  'ratio_rp_100_2055_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_100_2085_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_10_2055_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_10_2085_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_20_2055_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_20_2085_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_2_2055_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_2_2085_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_50_2055_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_50_2085_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_5_2055_2025_EC-EARTH3_percentage_change': '%',
  'ratio_rp_5_2085_2025_EC-EARTH3_percentage_change': '%',

  // ENSEMBLE
  ratio_rp_100_2055_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_100_2085_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_10_2055_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_10_2085_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_20_2055_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_20_2085_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_2_2055_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_2_2085_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_50_2055_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_50_2085_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_5_2055_2025_ENSEMBLE_percentage_change: '%',
  ratio_rp_5_2085_2025_ENSEMBLE_percentage_change: '%',
} as const

export type WaveUnit = keyof typeof WAVE_UNITS | 'hs_change'

export const DIRECTIONAL_ATTRIBUTES: Array<WaveUnit> = [
  'DirMEF',
  'extremes_energy_flux_averaged_direction',
] as const

export type WaveOption = { value: WaveUnit; label: string; description: string }

/** Grouped wave options for the Point attribute dropdown (tree with section headers) */
export const WAVES_OPTIONS_GROUPED: Array<{ header: string; options: WaveOption[] }> = [
  {
    header: 'Historic wave climate',
    options: [
      {
        value: 'hs_rp_100',
        label: 'Hₛ (RP 100 yr)',
        description: 'Significant wave height for a 100-year return period, in meters',
      },
      {
        value: 'hs_rp_50',
        label: 'Hₛ (RP 50 yr)',
        description: 'Significant wave height for a 50-year return period, in meters',
      },
      {
        value: 'Hs12',
        label: 'Hₛ₁₂',
        description: 'Significant wave height exceeded 12 hours per year, in meters',
      },
      {
        value: 'DirMEF',
        label: 'Mean Energy Flux Direction',
        description: 'Direction of mean wave energy flux, shown alongside the total wave power',
      },
      {
        value: 'extremes_energy_flux_averaged_direction',
        label: 'Extreme Energy Flux Direction',
        description: 'Direction of extreme wave energy flux, shown alongside the total wave power',
      },
      {
        value: 'WPsum',
        label: 'Total Wave Power',
        description: 'Total wave power, in watts per meter of wave crest (W/m)',
      },
    ],
  },
  {
    header: 'Wave climate projections',
    options: [
      {
        value: 'Hsmean_mean_percentage_change',
        label: 'Change in Mean Hₛ (%)',
        description:
          'Percentage change in mean significant wave height, projected up to 2050 across 6 climate models',
      },
      {
        value: 'Hs_q99_mean_percentage_change',
        label: 'Change in Hₛ 99th Pctl (%)',
        description:
          'Percentage change in 99th percentile significant wave height, projected up to 2050 across 6 climate models',
      },
      {
        value: 'hs_rp_100_mean_percentage_change',
        label: 'Change in Hₛ RP100 (%)',
        description:
          'Percentage change in significant wave height for a 100-year return period, projected up to 2050 across 6 climate models',
      },
      {
        value: 'WPmean_mean_percentage_change',
        label: 'Change in Mean Wave Power (%)',
        description:
          'Percentage change in mean wave power, projected up to 2050 across 6 climate models',
      },
      {
        value: 'hs_change',
        label: 'Projected Hₛ Change by RP',
        description:
          'Projected change in significant wave height in either 2055 or 2085 relative to historical conditions. Values represent the ensemble mean of ACCESS-CM2 and EC-EARTH3.',
      },
    ],
  },
]

/** Flat list of all wave options (derived from grouped); used for labels and default value lookup */
export const WAVES_OPTIONS: WaveOption[] = WAVES_OPTIONS_GROUPED.flatMap((g) => g.options)

export const wavePropertyLabels: Record<string, string> = Object.fromEntries(
  WAVES_OPTIONS.map((option) => [option.value, option.label])
)

export const RETURN_PERIODS = ['2', '5', '10', '20', '50', '100']

export const hsReturnPeriods = RETURN_PERIODS.map((rp) => ({
  value: rp,
  label: rp,
}))

export const DEPTHS = [10, 20, 50, 100]
